import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { Icon as Iconify } from '@iconify/react';
import { isEqual } from 'lodash';
import { useModulePathname } from "../../../layouts/main/ModuleNavigation";
import { useSchemeStoreAdd } from '../schemeStoreAdd';
import { renderControls } from '../utils/renderControls';
import ConfirmationPopup from '../Others/ConfirmationPopup2';
import PopUpHandle from '../../../commons/PopUpHandle';
import { getMenuPermissionsForPath, useMenuAccessStore } from '../../../store/menuAccessStore';


export default function SchemeDefinitionAdd() {
    const modulePathname = useModulePathname();

    const permissionsByRoute = useMenuAccessStore((state) => state.permissionsByRoute);
    const canAdd = getMenuPermissionsForPath(permissionsByRoute, modulePathname)?.add === true;

    const {
        schemeControls,
        primaryCategoryControls,
        executiveDefControls,
        schemeDivisionControls,
        criteriaHandlingControls,
        newCustomerControls,
        distributorBeneficiaryControls,
        parentSchemeDetails,
        fetchParentSchemeDetails,
        setSchemeData,
        setDefinitionData,
        validationErrorMessage,
        setLiftingUOMName,
        conversionRateControl,
        fetchConversionRateControl,
        pointBasedSlabRepeatationCriteriaControls,
        schemeData,
        addScheme
    } = useSchemeStoreAdd();

   

    const { handleSubmit, control, watch, setValue, reset, formState: { errors }, trigger } = useForm({
        shouldUnregister: true,
        shouldFocusError: true
    });

    const values = watch();

    useEffect(() => {
        if (schemeControls?.length) {
            const radioDefaults = {};
            schemeControls
                .filter(f => f.DataType === "RadioButton")
                .forEach(f => {
                    if (f.DefaultValues?.length > 0) {
                        radioDefaults[f.ValueExpr] = f.DefaultValues[0];
                    }
                });
            reset(radioDefaults);
        }
    }, [schemeControls, reset]);

    /*  ---------------------------- PopUp Message -------------------------------------------------------   */

    const [open, setOpen] = useState(false);
    const [errorr, setErrorr] = useState(0);
    const [errorCode, setErrorCode] = useState(null);


    const handleClose = () => {
        setOpen(false);
    };

    /* --------------------------------------------------------------------------------------  */


    const [confirmationPopupOpen, setConfirmationPopupOpen] = useState(false);

    const onConfirm = async () => {
        if (!values?.From_FromDate || !values?.To_ToDate) {
            setValue("IsApplySchemeConversion", false);
            return;
        }

        fetchConversionRateControl({
            FromDate: values.From_FromDate,
            ToDate: values.To_ToDate,
        });
    };

    // When user cancels popup or closes it
    const onDismiss = (type) => {
        if (type === "close") {
            setConfirmationPopupOpen(false);
        } else {
            setValue("IsApplySchemeConversion", false);
            setConfirmationPopupOpen(false);
        }
    };

    //  Validate conversion rate control after fetch
    useEffect(() => {
        if (!values?.From_FromDate || !values?.To_ToDate) {
            setValue("IsApplySchemeConversion", false);
            return;
        }

        if (!conversionRateControl?.length) {
            setValue("IsApplySchemeConversion", false);
            return;
        }

        const schemeConversionControl = conversionRateControl.find(
            (c) => c.ValueExpr === "SchemeConversionRateId"
        );

        if (
            schemeConversionControl &&
            Array.isArray(schemeConversionControl.DefaultValues) &&
            schemeConversionControl.DefaultValues.length === 0
        ) {
            setErrorCode(400);
            setErrorr(
                "As per selected scheme criteria, No matching conversion rate details found."
            );
            setOpen(true);
            setValue("IsApplySchemeConversion", false);
        } else if (schemeConversionControl) {
            setValue("IsApplySchemeConversion", true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversionRateControl, setValue]);

    useEffect(() => {
        if (values?.IsApplySchemeConversion) {
            if (!values?.From_FromDate || !values?.To_ToDate) {
                setErrorCode(400);
                setErrorr("From Date and To Date are required.");
                setOpen(true);
                setValue("IsApplySchemeConversion", false);
                return;
            }

            // Show popup only if dates exist
            // setConfirmationPopupOpen(true);
            fetchConversionRateControl({
                FromDate: values.From_FromDate,
                ToDate: values.To_ToDate,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.IsApplySchemeConversion]);





    useEffect(() => {
        if (values?.SchemeCriteria === 0) {
            setValue('SchemeCriteria', null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.SchemeCriteria])

    useEffect(() => {
        if (!isEqual(values, schemeData)) {
            setSchemeData(values);

        }



        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);


    useEffect(() => {
        if (values?.SchemeApplicable?.toString() === '1' && values?.SchemeBeneficiary?.toString() !== '2') {
            setValue('SchemeBeneficiary', 2);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);


    useEffect(() => {
        if (values?.ParentschemeId) {
            setValue('IsParentScheme', false);
        }
        if (values?.IsParentScheme) {
            setValue('ParentschemeId', null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.IsParentScheme, values?.ParentschemeId]);

    useEffect(() => {
        if (values?.ParentschemeId) {
            fetchParentSchemeDetails(values?.ParentschemeId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.ParentschemeId]);
    useEffect(() => {
        if (values?.ParentschemeId && parentSchemeDetails) {
            setValue('ExcludeException', parentSchemeDetails?.SchemeData?.ExcludeException || false);
            setValue('GroupApplicable', parentSchemeDetails?.SchemeData?.GroupApplicable || false);
            setValue('IsStandard', parentSchemeDetails?.SchemeData?.IsStandard || false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentSchemeDetails]);



    const onSubmit = async (data) => {

        let liftingUOMName = 'Target%'; // Default if not found
        const liftingUOMControl = schemeControls?.find(
            (controll) => controll.ValueExpr === 'LiftingUnitOfMeasurement'
        );

        if (liftingUOMControl) {
            const selectedLiftingUOM = liftingUOMControl.DefaultValues.find(
                (item) => item.LiftingUnitOfMeasurement === values.LiftingUnitOfMeasurement
            );
            liftingUOMName = selectedLiftingUOM ? selectedLiftingUOM.LiftingUnitOfMeasurementName : 'Target%';
        }
        await trigger();


        if (errors && Object.keys(errors).length > 0) {
            setDefinitionData('error');
            setLiftingUOMName(liftingUOMName)
        } else {
            setDefinitionData(data);
            setLiftingUOMName(liftingUOMName)
        }
        try {
            const result = await addScheme(); // NOSONAR
            console.log('result addScheme(): ', result);
        } catch (err) {
            // handle error
        }

    };





    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%', // Make sure parent takes full height
        }}>
            {open && (
                <PopUpHandle open={open} handleClose={handleClose} error={errorr} errorCode={errorCode} />
            )}
            <FormProvider >
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minHeight: 0, // Enables child to grow correctly inside a flex container
                    }}
                >
                    {/* Scrollable content */}
                    <div style={{
                        flexGrow: 1,
                        overflow: 'auto',
                        minHeight: 0,
                    }}>
                        <Box
                            sx={{
                                mt: 1,
                                flexGrow: 1,
                                rowGap: 2,
                                columnGap: 2,
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: '1fr 1fr',
                                    md: '1fr 1fr 1fr',
                                    lg: '1fr 1fr 1fr',
                                    xl: '1fr 1fr 1fr'
                                },
                                gridAutoRows: 'min-content',
                                paddingTop: '0.4rem',
                            }}
                        >
                            <style>
                                {`.MuiFormControlLabel-labelPlacementEnd { max-height: 20px; }`}
                            </style>

                            {schemeControls?.map((field, index) => {
                                if (field.ValueExpr === 'IsActive') return null;

                                return (
                                    <React.Fragment key={field.ValueExpr || index}>
                                        {renderControls(field, index, control, null, null, values)}

                                        {/* Inline dependent rendering */}
                                        {field.ValueExpr === 'SchemeApplicable' &&
                                            values?.SchemeApplicable?.toString() === '1' &&
                                            values?.SchemeBeneficiary?.toString() === '2' &&
                                            primaryCategoryControls?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}

                                        {field.ValueExpr === 'SchemeBeneficiary' &&
                                            values?.SchemeApplicable?.toString() === '2' &&
                                            values?.SchemeBeneficiary?.toString() === '2' &&
                                            distributorBeneficiaryControls?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}

                                        {field.ValueExpr === 'SchemeBeneficiary' &&
                                            values?.SchemeApplicable?.toString() === '2' &&
                                            values?.SchemeBeneficiary?.toString() === '4' &&
                                            executiveDefControls?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}

                                        {field.ValueExpr === 'NewCustomersOnly' &&
                                            values?.NewCustomersOnly &&
                                            newCustomerControls
                                                ?.filter((f) => {
                                                    // Always show all fields EXCEPT "Billng In N Months"
                                                    if (f.ValueExpr !== "NewCustomerBillngNMonths") return true;
                                                    // Show "Billng In N Months" only when billing type is "MinBillingInNMonths"
                                                    return values?.NewCustomerBilling === "MinBillingInNMonths";
                                                })
                                                ?.map((f, i) => renderControls(f, i, control, null, null, values))}


                                        {field.ValueExpr === 'IsSchemeDivisionWise' &&
                                            values?.IsSchemeDivisionWise &&
                                            schemeDivisionControls?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}
                                        {/* {field.ValueExpr === 'IsPointBasedScheme' && */}
                                        {/*    values?.IsPointBasedScheme && */}
                                        {/*    pointBasedSlabRepeatationCriteriaControls?.map((f, i) => */}
                                        {/*        renderControls(f, i, control, null, null, values) */}
                                        {/*    )} */}

                                        {/* added on 10July2026 */}
                                        {field.ValueExpr === 'IsRepeatSlab' &&
                                            values?.IsRepeatSlab &&
                                            pointBasedSlabRepeatationCriteriaControls?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}

                                        {field.ValueExpr === 'ParentschemeId' &&
                                            values?.ParentschemeId &&
                                            parentSchemeDetails?.SchemeData?.IsConditionalScheme &&
                                            criteriaHandlingControls?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}

                                        {field.ValueExpr === 'IsApplySchemeConversion' &&
                                            values?.IsApplySchemeConversion &&
                                            conversionRateControl?.map((f, i) =>
                                                renderControls(f, i, control, null, null, values)
                                            )}
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    </div>

                    {/* Divider */}
                    <Divider sx={{ mt: 2 }} />

                    {/* Save button fixed at the bottom */}
                    <Box sx={{ mt: 1 }}>
                        <Stack
                            direction="row"
                            justifyContent="flex-end"
                            spacing={2}
                            alignItems="center"
                            sx={{ padding: '2px' }}
                        >
                            {values?.AdditionalRebate && validationErrorMessage && (
                                <Typography color="red" sx={{ mr: 2, fontSize: '0.775rem' }}>
                                    {validationErrorMessage}
                                </Typography>
                            )}
                            {canAdd && <Tooltip title="Save">
                                <IconButton type="submit" sx={{
                                    bgcolor: '#43b5a2',
                                    color: '#fff',
                                    borderRadius: '4px',
                                    height: '30px',
                                    width: '31px',
                                    padding: '4px',
                                }}>
                                    <Iconify icon="bx:save" />
                                </IconButton>
                            </Tooltip>}
                        </Stack>
                    </Box>
                    {confirmationPopupOpen && (
                        <ConfirmationPopup
                            title="Confirmation"
                            msg="Scheme has no criteria, do you want to proceed?"
                            onConfirm={onConfirm}
                            onDismiss={onDismiss}
                        />
                    )}
                </form>
            </FormProvider>
        </div>
    );
}
